<template>
    <div class="content">
        <div class="container-fluid">
            <PageTitle :title="$t('BooksellerList.booksellerList')">
                <template v-slot:left>
                    <i class="fa fa-users fa-3x text-distribution"/>
                </template>
            </PageTitle>
            <transition mode="out-in" name="fade">
                <APILoading v-if="booksellers === undefined" key="loading" :color="$data.$colors.distribution" :text="$t('BooksellerList.loadingBooksellers')"/>
                <div class="row" v-else>
                    <div class="col-12">
                        <div class="card">
                            <div class="card-body">
                                <v-client-table class="col-12" :data="booksellers" :columns="table.columns" :options="tableOptions">
                                    <button @click="showBooksellerModal()" class="btn btn-primary pull-right" slot="afterLimit">
                                        <i class="fa fa-plus-circle mr-1"/>
                                        <span v-html="$t('BooksellerList.addBookseller')"/>
                                    </button>
                                    <router-link class="font-bold" slot="name" slot-scope="props" v-html="props.row.getFullName(hermesShop.type)"
                                                 :to="`bookseller/${props.row._id}`"/>
                                    <BooksellerActiveStatus slot-scope="props" slot="status" :bookseller="props.row"/>
                                    <BooleanIcon slot-scope="props" slot="isAdmin" :boolean="props.row.isAdmin"/>
                                    <span slot-scope="props" slot="connectedAt" v-html="properDateTime(props.row.connectedAt)"/>
                                    <div slot-scope="props" slot="actions">
                                        <router-link v-tooltip="$t('BooksellerList.see')" class="btn btn-info btn-xs" :to="`bookseller/${props.row._id}`">
                                            <i class="fa fa-eye"/>
                                        </router-link>
                                        <button v-tooltip="$t('BooksellerList.edit')" @click="showBooksellerModal(props.row)"
                                                class="btn btn-warning btn-xs ml-1">
                                            <i class="fa fa-edit"/>
                                        </button>
                                    </div>
                                </v-client-table>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
        <BooksellerFormModal ref="BooksellerFormModal" @addBookseller="addBookseller" @editBookseller="editBookseller"/>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { properDateTime } from "../../../../helpers/Date";
import PageTitle from "../../../shared/misc/PageTitle";
import Bookseller from "../../../../classes/Distribution/Bookseller/Bookseller";
import APILoading from "../../../shared/API/APILoading";
import { permissiveFilter } from "../../../../helpers/VueTable";
import BooksellerActiveStatus from "../../../shared/Distribution/Bookseller/BooksellerActiveStatus";
import BooleanIcon from "../../../shared/misc/BooleanIcon";
import BooksellerFormModal from "../../../shared/Distribution/Bookseller/BooksellerFormModal";

export default {
    name: "BooksellerList",
    components: {BooksellerFormModal, BooleanIcon, BooksellerActiveStatus, APILoading, PageTitle },
    data() {
        return {
            booksellers: undefined,
            table: {
                columns: ["name", "email", "accountingNumber", "status", "isAdmin", "connectedAt", "actions"],
            },
        };
    },
    computed: {
        ...mapGetters("hermesAPIConfig", {
            hermesShop: "shop",
        }),
        // eslint-disable-next-line max-lines-per-function
        tableOptions() {
            return {
                sortable: ["name", "email", "accountingNumber", "status", "isAdmin", "connectedAt"],
                perPage: 10,
                headings: {
                    email: this.$t("BooksellerList.email"),
                    name: this.nameHeading,
                    accountingNumber: this.$t("BooksellerList.accountingNumber"),
                    status: this.$t("BooksellerList.status"),
                    isAdmin: this.$t("BooksellerList.admin"),
                    connectedAt: this.$t("BooksellerList.connectedAt"),
                },
                orderBy: {
                    column: "name",
                },
                filterAlgorithm: {
                    name: (row, query) => permissiveFilter(row.getFullName(this.hermesShop.type), query),
                    status: (row, query) => permissiveFilter(row.activeStatusLabel, query),
                    connectedAt: (row, query) => permissiveFilter(this.properDateTime(row.connectedAt), query),
                },
                customSorting: {
                    name: ascending => (a, b) => {
                        if (ascending) {
                            return a.getFullName(this.hermesShop.type).toLowerCase() >= b.getFullName(this.hermesShop.type).toLowerCase() ? 1 : -1;
                        }
                        return a.getFullName(this.hermesShop.type).toLowerCase() <= b.getFullName(this.hermesShop.type).toLowerCase() ? 1 : -1;
                    },
                    status: ascending => (a, b) => {
                        if (ascending) {
                            return a.activeStatusLabel >= b.activeStatusLabel ? 1 : -1;
                        }
                        return a.activeStatusLabel <= b.activeStatusLabel ? 1 : -1;
                    },
                },
                cellClasses: {
                    isAdmin: [{
                        class: "text-center",
                        condition: () => true,
                    }],
                    status: [{
                        class: "text-center",
                        condition: () => true,
                    }],
                    actions: [{
                        class: "text-center",
                        condition: () => true,
                    }],
                },
            };
        },
        nameHeading() {
            if (this.hermesShop) {
                if (this.hermesShop.type === "B2B") {
                    return this.$t("BooksellerList.society");
                } else if (this.hermesShop.type === "B2C") {
                    return this.$t("BooksellerList.name");
                }
            }
            return "";
        },
    },
    async created() {
        if (!await this.checkUserAuthentication()) {
            return;
        }
        await this.getBooksellers();
    },
    methods: {
        ...mapActions("user", {
            checkUserAuthentication: "checkUserAuthentication",
        }),
        async getBooksellers() {
            try {
                const { data } = await this.$hermesAPI.getBooksellers();
                this.booksellers = data.map(bookseller => new Bookseller(bookseller));
            } catch (e) {
                this.$displayError(e);
            }
        },
        showBooksellerModal(bookseller = null) {
            this.$refs.BooksellerFormModal.show(bookseller);
        },
        addBookseller(bookseller) {
            this.booksellers.push(new Bookseller(bookseller));
        },
        editBookseller(updatedBookseller) {
            const idx = this.booksellers.findIndex(bookseller => bookseller._id === updatedBookseller._id);
            if (idx !== -1) {
                this.booksellers.splice(idx, 1, new Bookseller(updatedBookseller));
            }
        },
        properDateTime,
    },
};
</script>

<style scoped>

</style>